/**
 * ModeToggle Component
 * Segmented control for switching between Check-in and Note modes.
 * Renders as a rounded pill with two side-by-side segments.
 */

import { Text } from '@/components/ui/text';
import React from 'react';
import { Pressable, View } from 'react-native';

export type CheckInMode = 'check-in' | 'note';

export interface ModeToggleProps {
  /** Currently active mode */
  mode: CheckInMode;
  /** Callback when the mode changes */
  onChange: (mode: CheckInMode) => void;
  /** Whether the toggle is disabled */
  disabled?: boolean;
}

interface SegmentProps {
  label: string;
  isActive: boolean;
  onPress: () => void;
  disabled: boolean;
  accessibilityLabel: string;
}

function Segment({ label, isActive, onPress, disabled, accessibilityLabel }: SegmentProps) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      accessibilityState={{ selected: isActive, disabled }}
      className={`flex-1 items-center justify-center py-2 rounded-md ${
        isActive ? 'bg-primary' : ''
      }`}
    >
      <Text
        className={`text-sm font-medium ${
          isActive ? 'text-background' : 'text-muted-foreground'
        }`}
      >
        {label}
      </Text>
    </Pressable>
  );
}

export function ModeToggle({ mode, onChange, disabled = false }: ModeToggleProps) {
  return (
    <View className="mb-4">
      <View className="flex-row bg-surface border border-border rounded-lg p-1">
        <Segment
          label="Check-in"
          isActive={mode === 'check-in'}
          onPress={() => onChange('check-in')}
          disabled={disabled}
          accessibilityLabel="Switch to check-in mode"
        />
        <Segment
          label="Private Note"
          isActive={mode === 'note'}
          onPress={() => onChange('note')}
          disabled={disabled}
          accessibilityLabel="Switch to note mode"
        />
      </View>
      {mode === 'note' && (
        <Text className="text-sm text-muted-foreground mt-2 px-1 italic">
          Private notes are only visible to you. They won&apos;t count as a check-in or appear on any feeds.
        </Text>
      )}
    </View>
  );
}

export default ModeToggle;
